import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";
import { useContent, useSectionMeta } from "../content/ContentContext";
import { Reveal, Chapter } from "./Reveal";

const fmt = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export const SoundSection = () => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const { SITE } = useContent();
  const meta = useSectionMeta("sound");

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setTime(a.currentTime);
    const onMeta = () => setDuration(a.duration);
    const onEnd = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) {
      a.pause();
      setPlaying(false);
    } else {
      a.play();
      setPlaying(true);
    }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  return (
    <section id="sound" className="relative py-24 md:py-32" data-testid="sound-section">
      <audio ref={audioRef} src={SITE.sound.trackSrc} preload="metadata" data-testid="theme-audio-element" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Chapter number={meta.number} label={meta.label} id="sound" />
        </Reveal>
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-end">
          <Reveal delay={0.1}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight" data-testid="sound-heading">
              THE SOUND OF <span className="italic text-gold-light">THE SERIES</span>
            </h2>
            <p className="font-quote italic text-lg md:text-xl text-smoke mt-6" data-testid="sound-tagline">
              {SITE.sound.tagline}
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <div
              className="border border-white/10 bg-coal/70 backdrop-blur-sm rounded-2xl p-6 md:p-8"
              data-testid="audio-player"
            >
              <div className="flex items-center gap-5">
                <button
                  onClick={toggle}
                  className="shrink-0 w-14 h-14 rounded-full border border-gold/60 text-gold-light hover:bg-gold hover:text-ink transition-colors duration-300 flex items-center justify-center"
                  aria-label={playing ? "Pause theme" : "Play theme"}
                  data-testid="audio-play-button"
                >
                  {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5 fill-current" />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-base md:text-lg text-ivory truncate" data-testid="audio-track-title">
                    {SITE.sound.trackTitle}
                  </p>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-smoke mt-1">Original score</p>
                </div>
                <div className="flex items-end gap-[3px] h-6 shrink-0" aria-hidden="true">
                  {[0, 1, 2, 3].map((i) => (
                    <span
                      key={i}
                      className={`w-[3px] rounded-full bg-gold/80 ${playing ? "eq-bar" : "scale-y-[0.3]"}`}
                      style={{ height: "100%", animationDelay: `${i * 0.18}s` }}
                    />
                  ))}
                </div>
              </div>
              <div className="mt-6 flex items-center gap-3">
                <span className="text-[10px] tabular-nums text-smoke" data-testid="audio-current-time">{fmt(time)}</span>
                <div
                  className="relative flex-1 h-[3px] bg-white/10 rounded-full cursor-pointer group"
                  onClick={seek}
                  data-testid="audio-progress-track"
                >
                  <div
                    className="absolute inset-y-0 left-0 bg-gold rounded-full"
                    style={{ width: duration ? `${(time / duration) * 100}%` : "0%" }}
                  />
                </div>
                <span className="text-[10px] tabular-nums text-smoke" data-testid="audio-duration">{fmt(duration)}</span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
